import React from 'react';
import { Button, Checkbox, DropdonwFlags, InputText } from '@/components';
import { toCurrency } from '@/helpers/masks/toCurrency';
import PaymentType from '@/model/PaymentType';
import { updateMask as updateMaskCpfCnpj } from '@/helpers/masks/cpfCnpj';
import EventTrackingReport from '@/model/EventTrackingReport';
import { CustomTable, TableColumn } from '@/components/Table';
import dayjs from 'dayjs';
import EventMainTrackingReport from '@/model/EventMainTrackingReport';
import Page from '@/model/Page';
import Pagination from '@/components/Utils/Pagination';
import PaymentStatus from '@/model/PaymentStatus';
import { DataList } from '@/components/DataList';
import { DataColumn } from '@/features/reverse/types';
import { FormInputFilterTracking, OptionsPdv } from '@/features/reports/types';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import Section from '@/model/Section';
import UserType from '@/model/UserType';
import Search from '../../../../../assets/images/svg/search.svg';

export interface DataRowContractor {
  id: string;
  name: string;
  document: string;
  telephone: string;
  companyType: number;
  actions: string;
}

export interface TrackingContenttProps {
  formData: FormData;
  formErrors: FormErrors;
  pdvs?: OptionsPdv[];
  selectPdvs?: OptionsPdv[];
  tracking: EventMainTrackingReport | undefined;
  pagination: Page<EventTrackingReport, EventTrackingReport>;
  sections: Section[];
  selectSection: Section[];
  isFilter: boolean;
  userType: UserType;
  generatePDF: () => void;
  // onPage: (page: number) => void;
  onChangeFormInput: OnChangeFormInput;
  onChangeFilters: (id: string) => void;
  onChangeSection: (id: string) => void;
  clearFilters: () => void;
  submitFilter: (page: number) => void;
  onFilter: () => void;
  onPage: (page: number) => void;
}

export const TrackingContent: React.FC<TrackingContenttProps> = ({
  tracking,
  pagination,
  formData,
  formErrors,
  pdvs,
  selectPdvs,
  sections,
  isFilter,
  selectSection,
  userType,
  onChangeFormInput,
  // generatePDF,
  // onPage,
  onChangeFilters,
  clearFilters,
  submitFilter,
  onChangeSection,
  onFilter,
  onPage,
}) => {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const convertNumberToStatus = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.APPROVED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#7AD81B10' }}>
            <div style={{ color: '#7AD81B' }}>Aprovado</div>
          </div>
        );
      case PaymentStatus.DISAPPROVED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#E54F49' }}>Recusado</div>
          </div>
        );
      case PaymentStatus.AWAITING_PAYMENT:
        return (
          <div className="filterActive" style={{ backgroundColor: '#F6D93E10' }}>
            <div style={{ color: '#F6D93E' }}>Aguardando Pagamento</div>
          </div>
        );
      case PaymentStatus.CANCELED:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#3CAFC8' }}>Cancelado</div>
          </div>
        );
      case PaymentStatus.CANCELLATION_ANALYSIS:
        return (
          <div className="filterActive" style={{ backgroundColor: '#E54F4910' }}>
            <div style={{ color: '#3CAFC8' }}>Analisando solicitação de cancelamento</div>
          </div>
        );
      default:
        return (
          <div className="filterActive" style={{ backgroundColor: '#F6D93E10' }}>
            <div style={{ color: '#F6D93E' }}>Pendente</div>
          </div>
        );
    }
  };
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const convertPaymentTypeToString = (types: PaymentType[]) => {
    if (types) {
      if (types.length === 1) {
        switch (types[0]) {
          case PaymentType.CREDIT_CARD:
            return 'Crédito';
          case PaymentType.TWO_CREDIT_CARDS:
            return 'Dois cartões';
          case PaymentType.DEBIT_CARD:
            return 'Débito';
          case PaymentType.PIX:
            return 'Pix';
          case PaymentType.BANK_SLIP:
            return 'Boleto';
          case PaymentType.MONEY:
            return 'Dinheiro';
          case PaymentType.COURTESY:
            return 'Cortesia';
          default:
            return 'Outros';
        }
      } else {
        const dataColumnProfiles: DataColumn[] = [];
        types.forEach((data, index) => {
          dataColumnProfiles.push({
            id: `${index}`,
            name: convertPaymentTypeToString([data]) as string,
          });
        });
        return <DropdonwFlags pointerClass={true} dataColumn={dataColumnProfiles} />;
      }
    } else {
      return '';
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const mountTransaction = (transactions: string[]) => {
    if (transactions) {
      if (transactions.length === 1) {
        return transactions[0];
      }
      const dataColumnProfiles: DataColumn[] = [];
      transactions.forEach((data, index) => {
        dataColumnProfiles.push({
          id: `${index}`,
          name: data as string,
        });
      });
      return <DropdonwFlags pointerClass={true} dataColumn={dataColumnProfiles} />;
    }
    return '';
  };

  const columns: TableColumn<EventTrackingReport>[] = [
    {
      name: 'Cód',
      selector: row => row.id,
    },
    {
      name: 'PDV',
      selector: row => row.pdvName,
    },
    {
      name: 'Setor',
      selector: row => `${row.sectionName}`,
    },
    {
      name: 'Ingresso',
      selector: row => `${row.itemLabel ? row.itemLabel : row.ticketName}`,
      width: '320px',
    },
    {
      name: 'CPF',
      selector: row => (row.cpf ? updateMaskCpfCnpj(row.cpf) : '-----'),
    },
    {
      name: 'Venda',
      selector: row => dayjs(row.saleDate).format('DD/MM/YYYY [às] HH:mm'),
    },
    {
      name: 'Status',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selector: row => convertNumberToStatus(row.status) as unknown as PaymentStatus,
    },
    {
      name: 'Forma de pag',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selector: row => (row.types ? convertPaymentTypeToString(row.types) : '') as string,
    },
    {
      name: 'Valor',
      selector: row => toCurrency(row.value),
    },
    {
      name: 'Transação',
      selector: row => (row.transactions ? mountTransaction(row.transactions) : '') as string,
    },
  ];
  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 16 }}
      >
        <h5 style={{ margin: 0 }}>Rastreamento</h5>

        <div className="d-flex" style={{ gap: 10 }}>
          {/* <Button theme="outlineDark" onClick={() => generatePDF()} title="Exportar relatório" /> */}
          <Button theme="outlineDark" onClick={onFilter} title="Filtros" />
          {isFilter && <Button theme="outlineDark" onClick={clearFilters} title="Limpar filtros" />}
        </div>
      </div>
      <h6>Sumarizado</h6>
      <div
        className="customTableScroll p-4"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          gridColumnGap: '40px',
          overflow: 'auto',
          alignItems: 'end',
        }}
      >
        <DataList
          data={
            tracking
              ? [
                  {
                    title: 'Vendidos:',
                    content: tracking.amountSold ? `${tracking.amountSold}` : '-----',
                  },
                  {
                    title: 'Cortesias:',
                    content: tracking.amountCourtesy ? tracking.amountCourtesy : '-----',
                  },
                  {
                    title: 'Faturamento:',
                    content: tracking.billingValue
                      ? `${toCurrency(tracking.billingValue)}`
                      : '-----',
                  },
                  {
                    title: 'Descontos:',
                    content: tracking.discounts ? `${toCurrency(tracking.discounts)}` : '-----',
                  },
                  {
                    title: 'Faturamento com descontos:',
                    content: tracking.billingValue
                      ? `${toCurrency(tracking.billingValue)}`
                      : '-----',
                  },
                ]
              : []
          }
        />
      </div>
      <h6>Vendas por tipo de pagamento</h6>
      <div
        className="customTableScroll p-4"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr',
          gridColumnGap: '40px',
          overflow: 'auto',
          alignItems: 'end',
        }}
      >
        <DataList
          data={
            tracking
              ? [
                  {
                    title: 'Crédito:',
                    content: tracking.sumaryPaymenteMethod.creditCard
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.creditCard)}`
                      : '-----',
                  },
                  {
                    title: 'Debito:',
                    content: tracking.sumaryPaymenteMethod.debitCard
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.debitCard)}`
                      : '-----',
                  },
                  {
                    title: 'Pix:',
                    content: tracking.sumaryPaymenteMethod.pix
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.pix)}`
                      : '-----',
                  },
                  {
                    title: 'Dinheiro:',
                    content: tracking.sumaryPaymenteMethod.money
                      ? `${toCurrency(tracking.sumaryPaymenteMethod.money)}`
                      : '-----',
                  },
                ]
              : []
          }
        />
      </div>
      {isFilter && (
        <>
          <div
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid #5f5f5fff',
              backgroundColor: '#ffffffff',
              color: '#fff',
              fontWeight: 500,
              fontSize: '14px',
              transition: 'all 0.2s ease',
            }}
          >
            <h6 style={{ color: '#5f5f5fff', marginTop: 6 }}>Filtros</h6>
            <hr />
            {userType !== UserType.PDV && (
              <>
                <div className="input-label mt-3 mb-3">PDVs</div>
                <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                  {(pdvs ?? []).map(opt => (
                    <>
                      <div style={{ marginRight: 12 }}>
                        <Checkbox
                          key={String(opt.pdvID)}
                          name={`pdv-${opt.pdvID}`}
                          label={`-${String(opt.pdvName)}`}
                          checked={selectPdvs ? selectPdvs.some(s => s.pdvID === opt.pdvID) : false}
                          onChange={() => onChangeFilters(opt.pdvID)}
                        />
                      </div>
                    </>
                  ))}
                </div>
              </>
            )}
            <div className="input-label mt-4 mb-3">Setores</div>
            <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
              {(sections ?? []).map(section => (
                <>
                  <div style={{ marginRight: 12 }}>
                    <Checkbox
                      key={String(section.id)}
                      name={`section-${section.name}`}
                      label={`-${String(section.name)}`}
                      checked={selectSection ? selectSection.some(s => s.id === section.id) : false}
                      onChange={() => onChangeSection(section.id)}
                    />
                  </div>
                </>
              ))}
            </div>
            <div
              className="filters-grid  mt-5"
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr auto',
                gap: '16px',
                alignItems: 'end',
                marginTop: '16px',
              }}
            >
              <InputText
                name="itemLabel"
                type="text"
                label="Mesa"
                value={formData[FormInputFilterTracking.itemLabel]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.itemLabel)(e.target.value);
                }}
                error={formErrors.itemLabel && formErrors.itemLabel[0]}
              />

              <InputText
                name="orderDateStart"
                type="date"
                label="Data inicial"
                value={formData[FormInputFilterTracking.orderDateStart]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.orderDateStart)(e.target.value);
                }}
                error={formErrors.orderDateStart && formErrors.orderDateStart[0]}
              />

              <InputText
                name="orderDateEnd"
                type="date"
                label="Data final"
                value={formData[FormInputFilterTracking.orderDateEnd]}
                onChange={e => {
                  onChangeFormInput(FormInputFilterTracking.orderDateEnd)(e.target.value);
                }}
                error={formErrors.orderDateEnd && formErrors.orderDateEnd[0]}
              />

              <button
                type="button"
                onClick={() => submitFilter(pagination.page)}
                style={{
                  cursor: 'pointer',
                  height: 50,
                  padding: '0 12px',
                  boxSizing: 'border-box',
                  border: '1px solid #ccc',
                  borderRadius: 5,
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'end',
                  marginBottom: 55,
                }}
                aria-label="Buscar"
                title="Buscar"
              >
                <img src={Search} alt="" style={{ width: 24, height: 24 }} />
              </button>
            </div>
            <hr />
          </div>
        </>
      )}
      <div className="mt-5">
        <CustomTable
          progressPending={false}
          columns={columns}
          data={pagination.list || []}
          numberRowsPerPage={pagination.total || 0}
        />
        <Pagination
          currentPage={pagination.page}
          totalCount={pagination.total}
          pageSize={pagination.pageSize}
          onPageChange={page => onPage(page)}
          total={pagination.total}
        />
      </div>
    </>
  );
};
